// Petraloc content data — sourced from petraloc.com

export const BRAND = {
  name: "Petraloc",
  tagline: "Where language meets intelligence",
  email: "info@petraloc.com",
  phone: "+1 (917) 722-7038",
  phoneHref: "tel:+19177227038",
  address: {
    company: "Petraloc Global LLC",
    line1: "800 Third Avenue FRNT A #1085",
    city: "New York",
    region: "NY",
    postal: "10022",
    country: "United States",
  },
  logo: "/petraloc-color.png",
  logoColor: "/petraloc-color.png",
  logoWhite: "/petraloc-white.png",
  favicon: "/favicon.svg",
};

export const HERO_LANGUAGES = [
  { code: "us", word: "Hello", lang: "English" },
  { code: "fr", word: "Bonjour", lang: "French" },
  { code: "es", word: "Hola", lang: "Spanish" },
  { code: "pt", word: "Olá", lang: "Portuguese" },
  { code: "ht", word: "Bonjou", lang: "Haitian Creole" },
  { code: "cn", word: "你好", lang: "Mandarin" },
  { code: "sa", word: "مرحبا", lang: "Arabic" },
  { code: "jp", word: "こんにちは", lang: "Japanese" },
  { code: "kr", word: "안녕하세요", lang: "Korean" },
  { code: "de", word: "Hallo", lang: "German" },
  { code: "it", word: "Ciao", lang: "Italian" },
  { code: "ru", word: "Привет", lang: "Russian" },
  { code: "in", word: "नमस्ते", lang: "Hindi" },
  { code: "ke", word: "Jambo", lang: "Swahili" },
  { code: "tr", word: "Merhaba", lang: "Turkish" },
  { code: "nl", word: "Hallo", lang: "Dutch" },
  { code: "vn", word: "Xin chào", lang: "Vietnamese" },
  { code: "bd", word: "নমস্কার", lang: "Bengali" },
];

export const LANGUAGE_SERVICES = [
  {
    slug: "translation",
    title: "Translation",
    icon: "FileText",
    image: "https://images.unsplash.com/photo-1456324504439-367cee3b3c32?w=1200&auto=format&fit=crop",
    description: "Certified document, legal, medical, and technical translation in 85+ languages — every project reviewed by a second native linguist.",
  },
  {
    slug: "editing-proofreading",
    title: "Editing & Proofreading",
    icon: "PencilLine",
    image: "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/7alfynih_Document%20proofreading%20with%20red%20pen.jpg",
    description: "Independent review of translated content for accuracy, tone, terminology consistency, and target-market fluency.",
  },
  {
    slug: "localization",
    title: "Localization",
    icon: "Globe2",
    image: "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/osctw0id_Localization.jpg",
    description: "Adapt software, websites, marketing assets, and product content to the language, culture, and regulatory norms of each market.",
  },
  {
    slug: "interpretation",
    title: "Interpretation",
    icon: "Headphones",
    image: "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/f4w3xbqc_Business%20interpreters%20with%20headsets.jpg",
    description: "Simultaneous, consecutive, and remote interpretation for hearings, conferences, medical encounters, and corporate meetings.",
  },
  {
    slug: "transcription",
    title: "Transcription",
    icon: "Mic",
    image: "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/83gp8vda_Woman%20wearing%20headphones%20typing%20on%20laptop.png",
    description: "Verbatim, time-coded, or summarized transcription of legal, medical, corporate, and broadcast audio in any language.",
  },
  {
    slug: "subtitling-captioning",
    title: "Subtitling & Captioning",
    icon: "Subtitles",
    image: "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1200&auto=format&fit=crop",
    description: "Broadcast-grade subtitles, closed captions, and SDH compliant with ADA, FCC, and EU accessibility standards.",
  },
  {
    slug: "voice-over",
    title: "Voice Over",
    icon: "AudioLines",
    image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1200&auto=format&fit=crop",
    description: "Native-speaking voice talent for e-learning, IVR, corporate, and broadcast — recorded in professional studios.",
  },
];

export const AI_SERVICES = [
  {
    slug: "mtpe",
    title: "Machine Translation Post-Editing (MTPE)",
    shortTitle: "MTPE",
    icon: "ScanText",
    image: "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/lgjs4bnj_Post-editing%20machine%20translation%20workflow%20image.jpg",
    description: "Human linguists refine machine-translated output to publication quality — faster turnaround at a lower cost than full translation.",
  },
  {
    slug: "ai-content-review",
    title: "AI Content Review & Quality Assurance",
    shortTitle: "AI Content Review & QA",
    icon: "FileSearch",
    image: "https://images.unsplash.com/photo-1555421689-d68471e189f2?w=1200&auto=format&fit=crop",
    description: "Native specialists audit AI-generated multilingual content for accuracy, brand voice, factuality, and cultural appropriateness.",
  },
  {
    slug: "ai-localization-review",
    title: "AI Localization Review",
    shortTitle: "AI Localization Review",
    icon: "Languages",
    image: "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/sfvfyh1c_AI%20%2B%20multilingual%20globe%20on%20laptop.jpg",
    description: "Validate the linguistic and cultural quality of AI-localized products, websites, and apps before they reach your target market.",
  },
  {
    slug: "multilingual-ai-evaluation",
    title: "Multilingual AI Evaluation & Testing",
    shortTitle: "AI Evaluation & Testing",
    icon: "ClipboardCheck",
    image: "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/4gf8oykr_AI%20multilingual%20interface%20%20speech%20bubbles%20%20language%20testing%20visual.jpg",
    description: "Structured red-teaming, accuracy scoring, and human evaluation of LLMs and translation engines across 85+ languages.",
  },
  {
    slug: "ai-data-annotation",
    title: "AI Data Annotation & Linguistic Validation",
    shortTitle: "AI Data Annotation",
    icon: "Database",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop",
    description: "Multilingual data labeling, tagging, transcription, and validation — supplying clean training data for AI and LLM pipelines.",
  },
];

export const INDUSTRIES = [
  {
    slug: "healthcare",
    title: "Healthcare & Medical",
    short: "Medical & clinical documents",
    image: "https://images.pexels.com/photos/6129681/pexels-photo-6129681.jpeg",
    intro:
      "Certified translation and interpretation for hospitals, clinics, pharmaceutical companies, medical device manufacturers, and public health agencies.",
    useCases: [
      "Clinical trial documentation",
      "Patient consent forms & medical records",
      "Pharmaceutical labeling & IFUs",
      "Medical device manuals",
      "Remote medical interpretation",
      "Public health communications",
    ],
    services: ["Translation", "Editing & Proofreading", "Interpretation", "Transcription", "MTPE"],
  },
  {
    slug: "legal",
    title: "Legal & Compliance",
    short: "Court documents, contracts",
    image: "https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/e0vezhm8_Judge%20gavel%20%2B%20lawyer%20desk.jpg",
    intro:
      "Court-certified translation and interpretation for law firms, in-house counsel, courts, and immigration practices — with strict confidentiality and chain-of-custody.",
    useCases: [
      "Contracts & NDAs",
      "Court documents & depositions",
      "Immigration filings (USCIS)",
      "Patent & IP filings",
      "Regulatory compliance documents",
      "Remote & in-person legal interpretation",
    ],
    services: ["Translation", "Editing & Proofreading", "Interpretation", "Transcription"],
  },
  {
    slug: "finance",
    title: "Finance & Banking",
    short: "Financial reports, regulatory filings",
    image: "https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=1600&auto=format&fit=crop",
    intro:
      "Translation and localization for banks, asset managers, fintechs, and audit firms — built for regulatory precision and market-specific tone.",
    useCases: [
      "Annual reports & financial statements",
      "Prospectuses & investor communications",
      "KYC / AML documentation",
      "Banking apps & fintech localization",
      "Insurance policies",
      "Earnings calls transcription & subtitling",
    ],
    services: ["Translation", "Editing & Proofreading", "Localization", "MTPE", "Subtitling & Captioning"],
  },
  {
    slug: "government",
    title: "Government & NGOs",
    short: "Public sector & international aid",
    image: "https://images.pexels.com/photos/14128895/pexels-photo-14128895.jpeg",
    intro:
      "Trusted by government agencies, international organizations, and NGOs for sensitive multilingual communication across underserved language communities.",
    useCases: [
      "Public service announcements",
      "Election & voter materials",
      "Humanitarian aid documentation",
      "Refugee & resettlement services",
      "Diplomatic interpretation",
      "Policy briefs & reports",
    ],
    services: ["Translation", "Interpretation", "Voice Over", "Subtitling & Captioning"],
  },
  {
    slug: "technology",
    title: "Technology & AI",
    short: "Software localization, AI pipelines",
    image: "https://images.pexels.com/photos/1181354/pexels-photo-1181354.jpeg",
    intro:
      "Software, SaaS, and AI product localization — connecting your product to international users with idiomatic, on-brand language.",
    useCases: [
      "Software UI & mobile app localization",
      "Developer documentation",
      "Marketing websites & blogs",
      "AI training data & evaluation",
      "Knowledge base articles",
      "Product release notes",
    ],
    services: ["Translation", "Localization", "MTPE", "AI Localization Review", "AI Data Annotation"],
  },
  {
    slug: "education",
    title: "Education & eLearning",
    short: "Academic content, eLearning",
    image: "https://images.pexels.com/photos/37812751/pexels-photo-37812751.jpeg",
    intro:
      "Localization for schools, universities, edtech platforms, and academic publishers — making knowledge accessible across languages and cultures.",
    useCases: [
      "Course content & curricula",
      "eLearning modules & SCORM packages",
      "Academic transcripts & diplomas",
      "Research papers & journals",
      "Student handbooks",
      "Video lectures subtitling",
    ],
    services: ["Translation", "Localization", "Subtitling & Captioning", "Voice Over", "AI Content Review & QA"],
  },
];

// Additional industries — rendered on the dedicated /industries page only.
// Kept separate so the Home page (which maps over INDUSTRIES) remains untouched.
export const INDUSTRIES_EXTRA = [
  {
    slug: "media-entertainment",
    title: "Media & Entertainment",
    short: "Film, streaming, broadcast",
    image: "https://images.pexels.com/photos/2510428/pexels-photo-2510428.jpeg",
    intro:
      "End-to-end localization for studios, streaming platforms, broadcasters, and gaming publishers — preserving creative intent across every market.",
    useCases: [
      "Film & series subtitling",
      "Dubbing scripts & voice-over adaptation",
      "Streaming platform UI localization",
      "Press kits & marketing campaigns",
      "Video game localization & QA",
      "Closed captions (ADA, FCC, EU)",
    ],
    services: ["Translation", "Subtitling & Captioning", "Voice Over", "Localization", "Transcription"],
  },
  {
    slug: "ecommerce-retail",
    title: "E-commerce & Retail",
    short: "Product catalogs, storefronts",
    image: "https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg",
    intro:
      "Localization for global retailers and direct-to-consumer brands — adapting product content, storefronts, and campaigns to convert in every market.",
    useCases: [
      "Product descriptions & catalogs",
      "Shopify, Magento & WooCommerce storefronts",
      "Marketing campaigns & email flows",
      "Customer reviews & UGC moderation",
      "Returns, policies & customer support",
      "Mobile commerce app localization",
    ],
    services: ["Translation", "Localization", "MTPE", "AI Content Review & QA", "Editing & Proofreading"],
  },
  {
    slug: "travel-hospitality",
    title: "Travel & Hospitality",
    short: "Hotels, airlines, tourism",
    image: "https://images.pexels.com/photos/6492403/pexels-photo-6492403.jpeg",
    intro:
      "Multilingual content and interpretation for hotel groups, airlines, cruise lines, and tourism boards — building trust with international guests at every touchpoint.",
    useCases: [
      "Hotel & booking platform localization",
      "Destination guides & itineraries",
      "Airline IFE & safety materials",
      "Cruise line passenger communications",
      "Concierge & customer service interpretation",
      "Loyalty program & app content",
    ],
    services: ["Translation", "Localization", "Interpretation", "Voice Over", "Subtitling & Captioning"],
  },
];

// Full list (9) for the /industries listing and detail routes.
export const INDUSTRIES_ALL = [...INDUSTRIES, ...INDUSTRIES_EXTRA];

export const LANGUAGE_GROUPS = [
  {
    title: "Caribbean & Creole",
    subtitle: "Our Specialty",
    items: [
      { code: "ht", name: "Haitian Creole" },
      { code: "jm", name: "Jamaican Patois" },
      { code: "tt", name: "Trinidad English Creole" },
      { code: "mq", name: "Martinique Creole" },
      { code: "gp", name: "Guadeloupe Creole" },
      { code: "dm", name: "Dominican Creole" },
      { code: "lc", name: "Saint Lucian Creole" },
      { code: "aw", name: "Papiamento" },
      { code: "sr", name: "Sranan Tongo" },
      { code: "bz", name: "Belize Kriol" },
    ],
  },
  {
    title: "African Languages",
    items: [
      { code: "et", name: "Amharic" },
      { code: "so", name: "Somali" },
      { code: "ke", name: "Swahili" },
      { code: "sn", name: "Wolof" },
      { code: "ng", name: "Yoruba" },
      { code: "ng", name: "Igbo" },
      { code: "ng", name: "Hausa" },
      { code: "za", name: "Zulu" },
      { code: "zw", name: "Shona" },
      { code: "er", name: "Tigrinya" },
      { code: "et", name: "Oromo" },
      { code: "ml", name: "Bambara" },
      { code: "gh", name: "Twi" },
      { code: "cd", name: "Lingala" },
      { code: "rw", name: "Kinyarwanda" },
      { code: "za", name: "Afrikaans" },
      { code: "mg", name: "Malagasy" },
      { code: "mw", name: "Chichewa" },
      { code: "gn", name: "Fula" },
      { code: "ss", name: "Dinka" },
      { code: "bi", name: "Kirundi" },
      { code: "ls", name: "Sesotho" },
    ],
  },
  {
    title: "European Languages",
    items: [
      { code: "us", name: "English" },
      { code: "fr", name: "French" },
      { code: "es", name: "Spanish" },
      { code: "pt", name: "Portuguese" },
      { code: "it", name: "Italian" },
      { code: "de", name: "German" },
      { code: "nl", name: "Dutch" },
      { code: "pl", name: "Polish" },
      { code: "ru", name: "Russian" },
      { code: "ua", name: "Ukrainian" },
      { code: "ro", name: "Romanian" },
      { code: "se", name: "Swedish" },
      { code: "no", name: "Norwegian" },
      { code: "dk", name: "Danish" },
      { code: "fi", name: "Finnish" },
      { code: "gr", name: "Greek" },
      { code: "cz", name: "Czech" },
      { code: "hu", name: "Hungarian" },
      { code: "rs", name: "Serbian" },
      { code: "hr", name: "Croatian" },
      { code: "sk", name: "Slovak" },
      { code: "es", name: "Catalan" },
      { code: "bg", name: "Bulgarian" },
      { code: "lt", name: "Lithuanian" },
      { code: "lv", name: "Latvian" },
      { code: "ee", name: "Estonian" },
    ],
  },
  {
    title: "Asian Languages",
    items: [
      { code: "cn", name: "Mandarin Chinese" },
      { code: "jp", name: "Japanese" },
      { code: "kr", name: "Korean" },
      { code: "in", name: "Hindi" },
      { code: "bd", name: "Bengali" },
      { code: "in", name: "Tamil" },
      { code: "in", name: "Telugu" },
      { code: "th", name: "Thai" },
      { code: "vn", name: "Vietnamese" },
      { code: "id", name: "Indonesian" },
      { code: "my", name: "Malay" },
      { code: "ph", name: "Tagalog" },
      { code: "in", name: "Punjabi" },
      { code: "pk", name: "Urdu" },
      { code: "np", name: "Nepali" },
      { code: "lk", name: "Sinhala" },
      { code: "mm", name: "Burmese" },
      { code: "kh", name: "Khmer" },
      { code: "la", name: "Lao" },
      { code: "mn", name: "Mongolian" },
      { code: "cn", name: "Tibetan" },
      { code: "az", name: "Azerbaijani" },
      { code: "kz", name: "Kazakh" },
    ],
  },
  {
    title: "Middle Eastern Languages",
    items: [
      { code: "sa", name: "Arabic" },
      { code: "ir", name: "Persian (Farsi)" },
      { code: "af", name: "Dari" },
      { code: "af", name: "Pashto" },
      { code: "il", name: "Hebrew" },
      { code: "tr", name: "Turkish" },
      { code: "iq", name: "Kurdish" },
      { code: "uz", name: "Uzbek" },
      { code: "cn", name: "Uyghur" },
      { code: "pk", name: "Balochi" },
    ],
  },
];

export const TRUST_LOGOS = [
  "TheLocMasters", "Propio Language Services", "BIG Language Solutions",
  "ByLanguages", "Synergium", "LILT", "Hybrid Lynx", "Interlingual",
  "Traduality", "EasyTranslate", "Powerling", "WeLocalize",
  "Trusted Translations", "DUX Translations", "Go Global",
];

export const TIMELINE = [
  { year: "2024", title: "Foundation", body: "Petraloc concept developed — identifying the gap in Caribbean language services." },
  { year: "2025", title: "Launch", body: "Language Services, AI Solutions go live. Network expands to 85+ languages." },
  { year: "2026", title: "Expansion", body: "AI product suite grows. New markets in Caribbean and Francophone Africa." },
  { year: "2027", title: "Scale", body: "Full service group operational. International partnerships established." },
];

export const VALUES = [
  { title: "Precision", body: "Every deliverable is verified through a multi-stage review process by domain experts — not generalists.", icon: "Target" },
  { title: "Integrity", body: "Signed agreements, certified professionals, and transparent pricing — every engagement, no exceptions.", icon: "ShieldCheck" },
  { title: "Innovation", body: "AI-enhanced delivery across every division — without sacrificing the human judgment that machines cannot replicate.", icon: "Sparkles" },
  { title: "Inclusion", body: "We serve the languages and communities that the industry overlooks — Caribbean Creole, rare dialects, and underserved markets.", icon: "Globe2" },
  { title: "Care", body: "Our work affects real people — patients, immigrants, businesses. We treat every project with the seriousness it deserves.", icon: "Heart" },
  { title: "Excellence", body: "We hold ourselves to the highest standard because our clients cannot afford anything less.", icon: "Award" },
];

export const TESTIMONIALS = [
  {
    quote: "Petraloc delivered certified translations of our clinical trial protocols across 14 languages — on time, every time. The medical accuracy is what kept us coming back.",
    name: "Dr. Helena Vargas",
    role: "Director of Clinical Operations",
    industry: "Pharmaceutical",
  },
  {
    quote: "Court-certified, NDA-bound, and consistently fast. They handle our cross-border discovery work with the kind of rigor we expect from outside counsel itself.",
    name: "Marcus Whitfield",
    role: "Senior Litigation Counsel",
    industry: "Legal",
  },
  {
    quote: "We needed Haitian Creole and Wolof speakers for refugee resettlement documentation. Petraloc was the only firm that delivered native specialists, not back-translations.",
    name: "Amaya Okafor",
    role: "Program Lead, Humanitarian Response",
    industry: "Government & NGO",
  },
  {
    quote: "Their MTPE workflow cut our localization spend by roughly a third without compromising the brand voice — and the QA pass caught issues our AI vendor had missed.",
    name: "Lukas Brenner",
    role: "Head of Global Content",
    industry: "Technology",
  },
  {
    quote: "Annual reports in six languages, regulatory deadlines, zero margin for error. Petraloc has become an extension of our investor relations team.",
    name: "Priya Ramanathan",
    role: "VP, Investor Communications",
    industry: "Finance",
  },
  {
    quote: "Multilingual subtitles for 40+ video courses, delivered ahead of schedule and accessibility-compliant out of the box. Renewing for the next academic year.",
    name: "Sofia Almeida",
    role: "Director of Digital Learning",
    industry: "Education",
  },
];

export const COMPARISON = [
  {
    label: "Linguists",
    generic: "General translators",
    petraloc: "Native domain specialists",
  },
  {
    label: "Workflow",
    generic: "AI-only output",
    petraloc: "Human expertise + AI quality control",
  },
  {
    label: "Language coverage",
    generic: "Major languages only",
    petraloc: "Major languages + underserved markets",
  },
  {
    label: "Response time",
    generic: "Slow turnaround",
    petraloc: "Fast turnaround, dedicated PM",
  },
  {
    label: "Confidentiality",
    generic: "Generic NDA, if any",
    petraloc: "Signed NDA on every project",
  },
  {
    label: "Quality assurance",
    generic: "Single-pass delivery",
    petraloc: "Multi-stage review by native experts",
  },
];

export const TRUST_BAR = [
  { label: "85+ Languages" },
  { label: "Legal & Medical Specialists" },
  { label: "AI-Enhanced Quality Assurance" },
  { label: "Worldwide Coverage" },
  { label: "Fast Response Times" },
];
